import { registerSW } from 'virtual:pwa-register';
import type { RegisterSWOptions } from 'vite-plugin-pwa/types';

export const SERVICE_WORKER_UPDATE_INTERVAL_MS = 60 * 60 * 1000;

type RegisterSWFunction = (options?: RegisterSWOptions) => (reloadPage?: boolean) => Promise<void>;

interface RegisterAppServiceWorkerOptions {
    register?: RegisterSWFunction;
    setIntervalFn?: (handler: () => void, timeout: number) => number;
    updateIntervalMs?: number;
    onNeedReload?: () => void;
    onOfflineReady?: () => void;
    onRegisteredSW?: (swScriptUrl: string, registration: ServiceWorkerRegistration | undefined) => void;
    onRegisterError?: (error: unknown) => void;
}

const reloadPage = (): void => {
    window.location.reload();
};

const logRegistrationError = (error: unknown): void => {
    console.error('Service worker registration failed:', error);
};

export const registerAppServiceWorker = ({
    register = registerSW,
    setIntervalFn = window.setInterval.bind(window),
    updateIntervalMs = SERVICE_WORKER_UPDATE_INTERVAL_MS,
    onNeedReload = reloadPage,
    onOfflineReady,
    onRegisteredSW,
    onRegisterError = logRegistrationError,
}: RegisterAppServiceWorkerOptions = {}): ((reloadPage?: boolean) => Promise<void>) => {
    if (!('serviceWorker' in navigator)) {
        return async (): Promise<void> => undefined;
    }

    return register({
        immediate: true,
        onNeedReload,
        onOfflineReady,
        onRegisteredSW: (swScriptUrl, registration) => {
            onRegisteredSW?.(swScriptUrl, registration);

            if (!registration || updateIntervalMs <= 0) {
                return;
            }

            setIntervalFn(() => {
                registration.update().catch(onRegisterError);
            }, updateIntervalMs);
        },
        onRegisterError,
    });
};
